<template>
  <div
    class="card bg-base-100 rounded-lg shadow-xl flex flex-col p-4 pt-0 rounded-lg"
  >
    <div
      class="flex justify-between items-center sticky top-0 bg-base-100 z-10 py-4"
    >
      <div class="flex items-center gap-2">
        <button class="btn btn-sm btn-ghost btn-square" @click="toggleCollapse">
          <i
            class="mdi"
            :class="isCollapsed ? 'mdi--chevron-right' : 'mdi--chevron-down'"
          ></i>
        </button>
        <h3 class="text-lg font-semibold">Layers</h3>
      </div>
      <button class="btn btn-sm btn-ghost btn-square" @click="createNewLayer">
        <i class="iconify mdi--plus text-lg"></i>
      </button>
    </div>
    <!-- Main list container for all layers -->
    <div class="flex flex-col gap-2" :class="{ hidden: isCollapsed }">
      <!-- Individual layer item, becomes active when selected -->
      <div
        v-for="layer in layers"
        :key="layer.id"
        class="bg-base-200 hover:bg-base-300 rounded-lg p-2 cursor-pointer transition-colors"
        :class="{
          'bg-primary bg-opacity-10 border border-primary':
            selectedLayerId === layer.id,
        }"
        @click="$emit('update:selectedLayerId', layer.id)"
      >
        <div class="flex justify-between items-center gap-2">
          <!-- Left side: Layer name and color previews -->
          <div class="flex items-center gap-2 flex-1">
            <span
              class="font-medium text-base-content min-w-[60px] break-all"
              >{{ layer.name }}</span
            >
            <!-- Small preview squares showing colors used in this layer -->
            <div class="flex flex-wrap gap-0.5 max-w-[100px]">
              <div
                v-for="color in getLayerColors(layer)"
                :key="color"
                class="w-3 h-3 rounded border border-opacity-10 border-base-content hover:scale-110 transition-transform cursor-pointer"
                :style="{ backgroundColor: color }"
                :title="color"
                @click.stop="$emit('update:color', color)"
              ></div>
            </div>
          </div>
          <!-- Right side: Layer control buttons -->
          <div class="flex gap-1">
            <!-- Toggle layer visibility (eye icon) -->
            <button
              class="btn btn-sm btn-ghost btn-square"
              :class="{ 'opacity-50': !layer.visible }"
              @click.stop="toggleLayerVisibility(layer.id)"
            >
              <i class="iconify mdi--eye text-lg"></i>
            </button>
            <!-- Clear layer content (trash icon) -->
            <button
              class="btn btn-sm btn-ghost btn-square"
              @click.stop="clearLayer(layer.id)"
            >
              <i class="iconify mdi--delete text-lg"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { Layer } from "@/features/draw/types";
import { getLayerColors } from "@/features/draw/utils/layer/layerUtils";
import { ref } from "vue";

// Add collapsed state
const isCollapsed = ref(false);

// Props definition for component inputs
const props = defineProps<{
  layers: Layer[]; // Array of all layers
  selectedLayerId: number; // ID of currently selected layer
  uniqueCanvasColors: string[]; // List of all unique colors used in visible layers
  hasSelection: boolean; // Whether there is an active selection area
}>();

// Event emitter definition for component outputs
const emit = defineEmits<{
  (e: "update:selectedLayerId", layerId: number): void; // When layer selection changes
  (e: "update:color", color: string): void; // When color selection changes
  (e: "update:layerVisibility", update: { id: number; visible: boolean }): void; // When layer visibility toggles
  (e: "clear-layer", layerId: number): void; // When layer content is cleared
  (e: "create-layer"): void; // When creating a new layer
  (e: "delete-layer", layerId: number): void; // When deleting a layer
}>();

// Toggle collapsed state
const toggleCollapse = () => {
  isCollapsed.value = !isCollapsed.value;
};

// Create new layer
const createNewLayer = () => {
  emit("create-layer");
};

// Toggle visibility of specified layer
const toggleLayerVisibility = (layerId: number) => {
  const layer = props.layers.find((l) => l.id === layerId);
  if (!layer) return;
  emit("update:layerVisibility", { id: layerId, visible: !layer.visible });
};

// Clear content of specified layer and delete it with confirmation
const clearLayer = (layerId: number) => {
  const layer = props.layers.find((l) => l.id === layerId);
  if (!layer) return;
  if (confirm(`确定要删除 ${layer.name} 图层吗？`)) {
    emit("clear-layer", layerId);
    emit("delete-layer", layerId);
  }
};
</script>

<style scoped></style>
